<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, inject, ref, watch } from 'vue'
import Locality from '/@src/components/pages/zoppm/step6/Locality.vue'
import { useFieldArray } from 'vee-validate'
import { ProjektLokalita, ProjektLokalitaRolaEnum } from '/@src/client-portal'
import * as yup from 'yup'
import { getNode } from '@formkit/core'
import { useSteps } from '/@src/composable/useSteps'
import { useRoute } from 'vue-router'

export interface MiestoRealizacieProps {
  miestoNumber: number
}

defineProps<MiestoRealizacieProps>()

type PrevazujuceMiestoOptionType = {
  key: number
  nazov: string
}

const { t } = useI18n()
const isReadOnly = inject('isReadOnly')
const node = getNode('miesto realizácie')
const { setPrevNextStep, nextStep } = useSteps()
const { remove, push, fields, update } = useFieldArray<ProjektLokalita>(
  'projekt.miestaRealizacie'
)
const route = useRoute()

const localitiesHaveUniqueNames = computed(() => {
  const nazvy: string[] = []

  fields.value.forEach((field) => {
    if (field.value?.nazov) {
      nazvy.push(field.value?.nazov)
    }
  })

  return new Set(nazvy).size === nazvy.length
})

const duplicitneNazvySchema = computed(() =>
  yup.object({
    'duplicitne-nazvy': yup
      .boolean()
      .test('', 'Duplicitne nazvy', (value = true) => value),
  })
)

const prevazujuceMiestoSchema = computed(() =>
  yup.object({
    'prevazujuce-miesto': isReadOnly
      ? yup.boolean().optional()
      : yup.boolean().test('', t('errors.miestaRealizacie.empty'), (value) => !!value),
  })
)
const prevazujuceMiestoError = ref('')
const unikatneNazvyError = ref('')

const options = computed<ReadonlyArray<PrevazujuceMiestoOptionType>>(() => {
  return fields.value
    .map((field, index) => ({
      key: index,
      // Pouzijeme placeholder nazov ak zatial nebol vyplneny
      nazov: field.value?.nazov,
    }))
    .filter((option) => option.nazov)
})

const primarnaLokalita = computed<PrevazujuceMiestoOptionType | undefined>(() => {
  const primarnaLokalitaIndex = fields?.value?.findIndex(
    (field) =>
      field.value?.rola === ProjektLokalitaRolaEnum['PrimarnaLokalitaRealizacie'] &&
      field.value?.nazov
  )
  if (primarnaLokalitaIndex < 0) {
    return undefined
  }
  const lokalita = fields.value[primarnaLokalitaIndex]
  if (!lokalita) {
    return undefined
  }
  return {
    key: primarnaLokalitaIndex,
    nazov: lokalita.value?.nazov || `Miesto č. ${lokalita.value?.poradie! + 1}`,
  }
})

/**
 * Pridá novú lokalitu. Ak už nejaká existuje, dáta sa skopírujú.
 */
function addLokalita() {
  // Pridame prazdnu lokalitu
  push({
    rola: ProjektLokalitaRolaEnum['LokalitaRealizacie'],
  })
}

const validateDuplicateNames = async () => {
  await duplicitneNazvySchema.value
    .validate({
      'duplicitne-nazvy': localitiesHaveUniqueNames.value,
    })
    .then(() => (unikatneNazvyError.value = ''))
    .catch((err) => (unikatneNazvyError.value = err.message))
}

const validatePrevazujuceMiesto = async () => {
  await prevazujuceMiestoSchema.value
    .validate({
      'prevazujuce-miesto': primarnaLokalita.value !== undefined,
    })
    .then(() => (prevazujuceMiestoError.value = ''))
    .catch((err) => (prevazujuceMiestoError.value = err.message))
}

watch(localitiesHaveUniqueNames, validateDuplicateNames)
watch(primarnaLokalita, validatePrevazujuceMiesto)

// manualna validacia selectu, kedze nie je sucastou vee-validate, len nastavuje miestaRealizacie[n].rola
async function validateInput() {
  await validatePrevazujuceMiesto()
  await validateDuplicateNames()
}

async function onSubmit() {
  if (!isReadOnly) {
    // zvalidujeme select rucne, pretoze nie je sucastou vee-validate
    await validateInput()
    node?.submit()
  } else {
    setPrevNextStep(1)
  }
}

watch(
  fields,
  () => {
    // Aby pouzivatel videl aspon jeden field group, pushneme pri mounte objekt s prazdnymi hodnotami
    if (fields.value?.length === 0) {
      addLokalita()
    }

    // fields.value.forEach((field, index) => {
    //   // V pripade je rola `undefined`, nastavime default rolu
    //   update(index, {
    //     ...field.value,
    //     rola:
    //       !field.value?.rola
    //         ? ProjektLokalitaRolaEnum['LokalitaRealizacie']
    //         : field.value?.rola,
    //     poradie: index,
    //   })
    // })
  },
  { deep: true, immediate: true }
)

const handleUpdatePrevazujuceMiesto = async (miesto: PrevazujuceMiestoOptionType) => {
  const selectedLocalityId = miesto.key

  const primaryIndex = fields.value.findIndex(
    (field) => field.value?.rola === ProjektLokalitaRolaEnum['PrimarnaLokalitaRealizacie']
  )

  if (primaryIndex > -1) {
    update(primaryIndex, {
      ...fields.value[primaryIndex].value,
      rola: ProjektLokalitaRolaEnum['LokalitaRealizacie'],
    })
  }

  update(selectedLocalityId, {
    ...fields.value[selectedLocalityId].value,
    rola: ProjektLokalitaRolaEnum['PrimarnaLokalitaRealizacie'],
  })

  // fields.value.forEach((field, index) => {
  //   update(index, {
  //     ...field.value,
  //     rola:
  //       index === selectedLocalityId
  //         ? ProjektLokalitaRolaEnum['PrimarnaLokalitaRealizacie']
  //         : ProjektLokalitaRolaEnum['LokalitaRealizacie'],
  //   })
  // })
}
</script>

<template>
  <div class="localities mb-6">
    <div v-for="(field, idx) in fields" :key="idx">
      <IDSKDivider v-if="idx > 0" class="mt-8 mb-8" />
      <Locality
        :id="`projekt.miestaRealizacie[${idx}]`"
        :name="`projekt.miestaRealizacie[${idx}]`"
        :poradie="idx"
      />
      <IDSKLink
        v-if="fields.length > 1"
        class="mb-6"
        href="#"
        no-visited-state
        @click.prevent="remove(idx)"
      >
        {{ t('zoppm.step6.odstranit') }}
      </IDSKLink>
    </div>
  </div>

  <IDSKButton
    variant="secondary"
    class="mb-0"
    :data-test-id="`zoppm-lokality-pridat`"
    @click.prevent="addLokalita()"
  >
    {{ t('zoppm.step6.pridat') }}
  </IDSKButton>
  <IDSKDivider class="mt-8-! mb-8-!" />
  <IDSKHeading variant="m" class="mb-4">
    {{ t('zoppm.step6.prevazujuceMiesto') }}
  </IDSKHeading>

  <IDSKParagraph>{{ t('zoppm.step6.prevazujuceMiestoInfo') }}</IDSKParagraph>

  <div class="govuk-!-width-one-half">
    <IDSKSelect
      id="prevazujuce-miesto"
      :model-value="primarnaLokalita"
      :disabled="options.length === 0"
      :label="t('zoppm.step6.prevazujuceMiestoSelectText')"
      :options="options"
      full-width
      class="mb-6"
      option-object-value="key"
      option-object-label="nazov"
      name="prevazujuce-miesto"
      :error-message="prevazujuceMiestoError"
      @update:model-value="handleUpdatePrevazujuceMiesto"
      @blur="validateInput"
    />
  </div>

  <IDSKErrorSummary
    v-if="unikatneNazvyError"
    :title="t('zoppm.step6.duplicateNamesErrorTitle')"
  >
    <template #description>
      {{ t('zoppm.step6.duplicateNamesErrorDescription') }}
    </template>
  </IDSKErrorSummary>

  <IDSKButtonGroup v-if="!route.query?.ref">
    <IDSKButton
      v-if="isReadOnly"
      type="button"
      variant="primary"
      :disabled="false"
      :data-test-id="`zoppm-next-${nextStep}`"
      @click.prevent="onSubmit"
    >
      {{ t('dalejNa', { name: nextStep }) }}
    </IDSKButton>
    <IDSKButton
      v-else
      type="button"
      variant="primary"
      :disabled="!primarnaLokalita"
      :data-test-id="`zoppm-back-${nextStep}`"
      @click.prevent="onSubmit"
    >
      {{ t('dalejNa', { name: nextStep }) }}
    </IDSKButton>

    <IDSKLink href="#" @click.prevent="setPrevNextStep(-1)">
      {{ t('spatNaKrok') }}
    </IDSKLink>
  </IDSKButtonGroup>
</template>

<i18n lang="yaml">
sk:
  dalejNa: 'Ďalej na {name}'
  spatNaKrok: 'Späť na predchádzajúci krok'
</i18n>
